'use client';

import { useEffect, useState, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import api from '@/lib/axios';
import ProductCard from '@/components/product/ProductCard';

interface Urun {
  id: number;
  urun_ad: string;
  marka: string;
  urun_slug?: string;
  urun_resimleri: { dosya_yolu: string; alt_metin: string }[];
  urun_varyasyonlari: { id: number; varyasyon_ad: string; satis_fiyati: number; piyasa_fiyati: number; stok: number }[];
  kategori: { ad: string; slug: string };
}

interface Meta {
  toplam: number;
  sayfa: number;
  limit: number;
  toplam_sayfa: number;
}

function UrunlerIcerik() {
  const searchParams = useSearchParams();
  const arama = searchParams.get('arama') || '';

  const [urunler, setUrunler] = useState<Urun[]>([]);
  const [meta, setMeta] = useState<Meta | null>(null);
  const [sayfa, setSayfa] = useState(1);
  const [yukleniyor, setYukleniyor] = useState(true);

  useEffect(() => {
    setSayfa(1);
  }, [arama]);

  useEffect(() => {
    setYukleniyor(true);
    const params = new URLSearchParams();
    params.set('sayfa', String(sayfa));
    params.set('limit', '20');
    params.set('aktif', 'true');
    if (arama) params.set('arama', arama);

    api.get(`/urunler?${params.toString()}`).then((res) => {
      setUrunler(res.data.veri || []);
      setMeta(res.data.meta);
      setYukleniyor(false);
    });
  }, [sayfa, arama]);

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[var(--color-gray-800)]">
          {arama ? `"${arama}" için sonuçlar` : 'Tüm Ürünler'}
        </h1>
        {meta && (
          <span className="text-sm text-[var(--color-gray-500)]">{meta.toplam} ürün</span>
        )}
      </div>

      {yukleniyor ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(12)].map((_, i) => (
            <div key={i} className="bg-[var(--color-gray-200)] animate-pulse rounded-[var(--radius-xl)] h-72" />
          ))}
        </div>
      ) : urunler.length === 0 ? (
        <div className="text-center py-20 text-[var(--color-gray-500)]">
          {arama ? `"${arama}" için ürün bulunamadı.` : 'Henüz ürün bulunmuyor.'}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {urunler.map((urun) => (
              <ProductCard
                key={urun.id}
                id={urun.id}
                urun_ad={urun.urun_ad}
                marka={urun.marka}
                resim={urun.urun_resimleri?.[0]?.dosya_yolu}
                resim_alt={urun.urun_resimleri?.[0]?.alt_metin}
                varyasyonlar={urun.urun_varyasyonlari}
                kategori={urun.kategori?.ad}
                href={urun.urun_slug ? `/${urun.urun_slug}` : `/urunler/${urun.id}`}
              />
            ))}
          </div>

          {meta && meta.toplam_sayfa > 1 && (
            <div className="flex justify-center gap-2 mt-8 flex-wrap">
              <button
                onClick={() => setSayfa(Math.max(1, sayfa - 1))}
                disabled={sayfa === 1}
                className="px-4 py-2 border border-[var(--color-gray-200)] rounded-[var(--radius-lg)] disabled:opacity-50 hover:border-[var(--color-primary)] transition text-sm"
              >
                ← Önceki
              </button>
              {[...Array(meta.toplam_sayfa)].map((_, i) => (
                <button
                  key={i}
                  onClick={() => setSayfa(i + 1)}
                  className={`w-10 h-10 border rounded-[var(--radius-lg)] text-sm font-medium transition ${
                    sayfa === i + 1
                      ? 'bg-[var(--color-primary)] text-white border-[var(--color-primary)]'
                      : 'border-[var(--color-gray-200)] hover:border-[var(--color-primary)]'
                  }`}
                >
                  {i + 1}
                </button>
              ))}
              <button
                onClick={() => setSayfa(Math.min(meta.toplam_sayfa, sayfa + 1))}
                disabled={sayfa === meta.toplam_sayfa}
                className="px-4 py-2 border border-[var(--color-gray-200)] rounded-[var(--radius-lg)] disabled:opacity-50 hover:border-[var(--color-primary)] transition text-sm"
              >
                Sonraki →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function UrunlerPage() {
  return (
    <Suspense fallback={
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(12)].map((_, i) => (
            <div key={i} className="bg-gray-200 animate-pulse rounded-xl h-72" />
          ))}
        </div>
      </div>
    }>
      <UrunlerIcerik />
    </Suspense>
  );
}